import React, { useState, useEffect, createRef, useContext } from 'react';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import styled from 'styled-components';
import cogoToast from 'cogo-toast';
import {
  SubNavigation,
  BackButton,
  ActionButton,
  LoadingSpinner,
  Alert,
} from 'wg-fe-ui';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router';
import { FlagsProvider } from '../../hoc/FlagsProviderHoc';
import { getMeData } from '../../services/meDataService';
import Frame from '../../../assets/images/Frame.svg';

import DriversList from '../../components/CRMRiskObjectDetailCarDrivers';
import CarInformation from '../../components/CRMRiskObjectDetailCarInformation';
// import CarAssistance from '../components/CRMRiskObjectDetailCarAssistance';
// import CarCategory from '../components/CRMRiskObjectDetailCarCategory';
import { TopNavigationItems } from '../../services/context/TopNavigationBarContext';
import { getBrokerData } from '../../services/brokerDataService';
import {
  getRiskObjectByTypeAndId,
  createRiskObjectRevision,
} from '../../services/apiRouterService';

const CRMRiskObjectDetailCar = () => {
  const location = useLocation();
  const { setNavTabs } = useContext(TopNavigationItems);
  const [isLoading, setIsLoading] = useState(true);
  const configCatClient = useContext(FlagsProvider);
  const { t } = useTranslation();
  const history = useHistory();
  const refs = {};
  const [carData, setCarData] = useState(location?.state?.updatedCarData ?? {});
  const [updatedCarData, setUpdatedCarData] = useState(
    location?.state?.updatedCarData ?? {},
  );
  const [updateRequired, setUpdateRequired] = useState(false);
  const [isCallantFeature, setIsCallantFeature] = useState();
  const [isProFlowEnabled, setIsProfFlowEnabled] = useState();

  const [canEditOffers, setCanEditOffers] = useState(false);

  const [currentSection, setCurrentSection] = useState({
    section: 1,
    subsection: 1,
  });

  const {
    id: riskObjectId,
    revision: riskObjectRevision,
    offerId,
    contractCaseId,
    contractId,
    prospectId,
    leadId,
    riskObjectType,
    offerRevision,
    editing,
  } = useParams();

  useEffect(() => {
    setFeatureToggles();
    setNavTabs([]);
    if (location?.state?.updatedCarData) return setIsLoading(false);
    retrieveVehicleData();
  }, []);

  async function setFeatureToggles() {
    const { me } = (await getMeData()) || {};
    const { broker } = (await getBrokerData()) || {};

    const emailConfig = {
      email: me?.email,
      custom: {
        plan: broker?.plan,
      },
    };

    if (offerId || contractId || prospectId) {
      setCanEditOffers(
        leadId
          ? false
          : await configCatClient.getValueAsync(
              offerId || contractId
                ? 'editOffers'
                : 'canEditProspectsAndRiskObjects',
              false,
              emailConfig,
            ),
      );
    }

    setIsCallantFeature(
      await configCatClient.getValueAsync('callantFeature', true, emailConfig),
    );

    setIsProfFlowEnabled(
      await configCatClient.getValueAsync('professionalFlow', false, {
        email: me?.email,

        custom: {
          plan: broker?.plan,
        },
      }),
    );
  }

  async function retrieveVehicleData() {
    const { distribution_id } = await getBrokerData();
    const [response, status] = await getRiskObjectByTypeAndId(
      distribution_id,
      'cars',
      riskObjectId,
      riskObjectRevision,
    );
    if (status !== 200) {
      if (status !== 404) {
        return console.error(response);
      }
      console.log('FAILED TO LOAD');
      setIsLoading(false);
      setCarData(null);
      return;
    }

    setCarData(response);
    setUpdatedCarData(response);
    setIsLoading(!isLoading);
  }

  const [sections] = useState([
    {
      id: 1,
      label: `${t('Risk object')} - ${t('Vehicles.Vehicle')}`,
      subsections: [
        {
          id: 1,
          label: t('Vehicle information'),
        },
        {
          id: 2,
          label: t('Drivers'),
        },
      ],
    },
  ]);

  const handleSideBarClick = (section, sub) => {
    const yCoordinate =
      refs[section][sub].current.getBoundingClientRect().top +
      window.pageYOffset;

    window.scrollTo({
      top: yCoordinate - 160,
      behavior: 'smooth',
    });
  };

  const handleScroll = () => {
    const currentScroll = window.pageYOffset;
    // Iterate over sections refs
    for (const [key, val] of Object.entries(refs)) {
      // Iterate over subsections refs
      for (const [k, v] of Object.entries(val)) {
        if (v.current && currentSection !== k) {
          const currentElScroll =
            v.current.getBoundingClientRect().top + currentScroll;
          if (currentElScroll > currentScroll) {
            setCurrentSection({ section: Number(key), subsection: Number(k) });
            return;
          }
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  });

  sections.forEach(s => {
    refs[s.id] = {};

    s.subsections.forEach(value => {
      refs[s.id][value.id] = createRef();
    });
  });

  const carInformationSave = values => {
    const payload = {
      age: isNaN(parseInt(values?.age ? values?.age : updatedCarData.age))
        ? null
        : parseInt(values?.age ? values?.age : updatedCarData.age),
      average_km_per_year: isNaN(
        parseInt(
          values?.average_km_per_year
            ? values?.average_km_per_year
            : updatedCarData.average_km_per_year,
        ),
      )
        ? null
        : parseInt(
            values?.average_km_per_year
              ? values?.average_km_per_year
              : updatedCarData.average_km_per_year,
          ),
      brand: values?.brand ? values?.brand : updatedCarData.brand,
      category: values?.category ? values?.category : updatedCarData.category,
      cc: parseInt(values?.cc ? values?.cc : updatedCarData.cc),
      co2: parseInt(values?.co2 ? values?.co2 : updatedCarData.co2),
      color: values?.color ? values?.color : updatedCarData.color,
      country_of_registration: values?.country_of_registration
        ? values?.country_of_registration
        : updatedCarData.country_of_registration,
      drive_assistance_system: {
        attention_assist:
          values?.attention_assist !== null &&
          values?.attention_assist !== undefined
            ? values?.attention_assist
            : updatedCarData.drive_assistance_system.attention_assist,
        adaptive_cruise_control:
          values?.adaptive_cruise_control !== null &&
          values?.adaptive_cruise_control !== undefined
            ? values?.adaptive_cruise_control
            : updatedCarData.drive_assistance_system.adaptive_cruise_control,
        automatic_emergency_braking:
          values?.automatic_emergency_braking !== null &&
          values?.automatic_emergency_braking !== undefined
            ? values?.automatic_emergency_braking
            : updatedCarData.drive_assistance_system
                .automatic_emergency_braking,
        automatic_parking:
          values?.automatic_parking !== null &&
          values?.automatic_parking !== undefined
            ? values?.automatic_parking
            : updatedCarData.drive_assistance_system.automatic_parking,
        blind_spot_check:
          values?.blind_spot_check !== null &&
          values?.blind_spot_check !== undefined
            ? values?.blind_spot_check
            : updatedCarData.drive_assistance_system.blind_spot_check,
        ecall:
          values?.ecall !== null && values?.ecall !== undefined
            ? values?.ecall
            : updatedCarData.drive_assistance_system.ecall,
        lane_support:
          values?.lane_support !== null && values?.lane_support !== undefined
            ? values?.lane_support
            : updatedCarData.drive_assistance_system.lane_support,
        parking_aid:
          values?.parking_aid !== null && values?.parking_aid !== undefined
            ? values?.parking_aid
            : updatedCarData.drive_assistance_system.parking_aid,
      },
      drivers: [
        {
          // accident_statement:
          //   values?.accident_statement != null
          //     ? values?.accident_statement
          //     : updatedCarData.drivers[0].accident_statement != null
          //     ? updatedCarData.drivers[0].accident_statement
          //     : null,
          accident_statement: null,
          address:
            values?.address != null
              ? values?.address
              : updatedCarData.drivers[0].address != null
              ? updatedCarData.drivers[0].address
              : null,
          birth: values?.birth || updatedCarData.drivers[0].birth || null,
          birth_place: null,
          // values?.birth_place != null
          //   ? values?.birth_place
          //   : updatedCarData.drivers[0].birth_place != null
          //   ? updatedCarData.drivers[0].birth_place
          //   : null,
          driver_license_type:
            values?.driver_license_type != null
              ? values?.driver_license_type
              : updatedCarData.drivers[0].driver_license_type != null
              ? updatedCarData.drivers[0].driver_license_type
              : null,
          first_name:
            values?.first_name != null
              ? values?.first_name
              : updatedCarData.drivers[0].first_name != null
              ? updatedCarData.drivers[0].first_name
              : null,
          home_to_work_distance: null,
          // values?.home_to_work_distance != null
          //   ? values?.home_to_work_distance
          //   : updatedCarData.drivers[0].home_to_work_distance != null
          //   ? updatedCarData.drivers[0].home_to_work_distance
          //   : null,
          issue_license_date:
            values?.issue_license_date != null
              ? values?.issue_license_date
              : updatedCarData.drivers[0].issue_license_date != null
              ? updatedCarData.drivers[0].issue_license_date
              : null,
          last_name:
            values?.last_name != null
              ? values?.last_name
              : updatedCarData.drivers[0].last_name != null
              ? updatedCarData.drivers[0].last_name
              : null,
          number_of_accidents_last_five_years: parseInt(
            values?.number_of_accidents_last_five_years != null
              ? values?.number_of_accidents_last_five_years
              : updatedCarData.drivers[0].number_of_accidents_last_five_years !=
                null
              ? updatedCarData.drivers[0].number_of_accidents_last_five_years
              : null,
          ),
          primary:
            values?.primary != null
              ? values?.primary
              : updatedCarData.drivers[0].primary != null
              ? updatedCarData.drivers[0].primary
              : null,
          profession:
            values?.profession != null
              ? values?.profession
              : updatedCarData.drivers[0].profession != null
              ? updatedCarData.drivers[0].profession
              : null,
          special_employment:
            values?.special_employment != null
              ? values?.special_employment
              : updatedCarData.drivers[0].special_employment != null
              ? updatedCarData.drivers[0].special_employment
              : false,
        },
      ],
      febiacid: values?.febiacid ? values?.febiacid : updatedCarData.febiacid,
      invoice_value: parseFloat(
        values?.invoice_value
          ? values?.invoice_value
          : updatedCarData.invoice_value,
      ),
      is_cabrio:
        values?.is_cabrio !== null && values?.is_cabrio !== undefined
          ? values?.is_cabrio
          : updatedCarData.is_cabrio,
      is_coupe:
        values?.is_coupe !== null && values?.is_coupe !== undefined
          ? values?.is_coupe
          : updatedCarData.is_coupe,
      is_jeep:
        values?.is_jeep !== null && values?.is_jeep !== undefined
          ? values?.is_jeep
          : updatedCarData.is_jeep,
      is_second_hand:
        values?.is_second_hand !== null && values?.is_second_hand !== undefined
          ? values?.is_second_hand
          : updatedCarData.is_second_hand,
      is_sportscar:
        values?.is_sportscar !== null && values?.is_sportscar !== undefined
          ? values?.is_sportscar
          : updatedCarData.is_sportscar,
      kw: isNaN(parseInt(values?.kw ? values?.kw : updatedCarData.kw))
        ? null
        : parseInt(values?.kw ? values?.kw : updatedCarData.kw),
      kwh: isNaN(parseInt(values?.kwh ? values?.kwh : updatedCarData.kwh))
        ? null
        : parseInt(values?.kwh ? values?.kwh : updatedCarData.kwh),
      license_plate_type: values?.license_plate_type
        ? values?.license_plate_type
        : updatedCarData.license_plate_type,
      model: values?.model ? values?.model : updatedCarData.model,
      model_year: isNaN(
        parseInt(
          values?.model_year ? values?.model_year : updatedCarData.model_year,
        ),
      )
        ? null
        : parseInt(
            values?.model_year ? values?.model_year : updatedCarData.model_year,
          ),
      motor_type: values?.motor_type
        ? values?.motor_type
        : updatedCarData.motor_type,
      name: values?.name ? values?.name : updatedCarData.name,
      number_plate: values?.number_plate
        ? values?.number_plate
        : updatedCarData.number_plate,
      risk_object_id: updatedCarData?.risk_object_id,
      risk_object_revision_id: updatedCarData?.risk_object_revision_id,
      options_value: isNaN(
        parseFloat(
          values?.options_value
            ? values?.options_value
            : updatedCarData.options_value,
        ),
      )
        ? null
        : parseFloat(
            values?.options_value
              ? values?.options_value
              : updatedCarData.options_value,
          ),
      registration_first: values?.registration_first
        ? values?.registration_first
        : updatedCarData.registration_first,
      registration_last: values?.registration_last
        ? values?.registration_last
        : updatedCarData.registration_last,
      seats: values?.seats ? values?.seats : updatedCarData.seats,
      used_for: values?.used_for ? values?.used_for : updatedCarData.used_for,
      value: isNaN(
        parseFloat(values?.value ? values?.value : updatedCarData.value),
      )
        ? null
        : parseFloat(values?.value ? values?.value : updatedCarData.value),
      vat_regime: values?.vat_regime
        ? values?.vat_regime
        : updatedCarData.vat_regime,
      vehicle_type: values?.vehicle_type
        ? values?.vehicle_type
        : updatedCarData.vehicle_type,
      version: values?.version ? values?.version : updatedCarData.version,
    };

    setUpdatedCarData(payload);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    if (!isEqual(carData, payload)) {
      showUpdateMessage();
    }
  };

  const showUpdateMessage = () => {
    setUpdateRequired(true);
  };

  const handleSaveCarOfferChanges = () => {
    const pathname = prospectId
      ? `/sales-management/customer/${prospectId}/contracts/offer/${offerId}/${offerRevision}`
      : `/sales-management/offer/${offerId}/${offerRevision}`;
    history.push({
      pathname,
      state: {
        ...location.state,
        updatedCarData: updatedCarData,
      },
    });
  };

  const handleSaveCarContractChanges = () => {
    const pathname = prospectId
      ? `/sales-management/customer/${prospectId}/contracts/case/${contractCaseId}/contract/${contractId}`
      : `/sales-management/case/${contractCaseId}/contract/${contractId}`;
    history.push({
      pathname,
      state: {
        ...location.state,
        updatedCarData: updatedCarData,
      },
    });
  };

  const handleSaveCarChanges = async () => {
    const { distribution_id } = await getBrokerData();
    const tempUpdatedCarData = cloneDeep(updatedCarData);
    delete tempUpdatedCarData.id;
    delete tempUpdatedCarData.risk_object_id;
    delete tempUpdatedCarData.risk_object_revision_id;
    let [response, status] = await createRiskObjectRevision(
      distribution_id,
      prospectId,
      'cars',
      riskObjectId,
      tempUpdatedCarData,
    );

    if (!(status < 204 && status >= 200)) {
      cogoToast.error(t('Something went wrong'));
      return console.error(response);
    }

    cogoToast.success(t('Risk object revision successfully saved'));

    setUpdateRequired(false);
    history.push({
      pathname:
        prospectId && leadId
          ? `/sales-management/customer/${prospectId}/contracts/lead/${leadId}/risk-object/${riskObjectType}/${riskObjectId}/${response.risk_object_revision_id}`
          : `/sales-management/customer/${prospectId}/risk-object/${riskObjectType}/${riskObjectId}/${response.risk_object_revision_id}`,
      state: { ...location.state },
    });
  };

  const startNewTarrification = () => {
    history.push({
      pathname: `/professional-flow/new-tarrification-prospect/`,
      search: `?prospectId=${prospectId}`,
      state: { type: 'car', riskObjectId },
    });
  };

  const backButtonHandler = () => {
    let pathname = '';
    if (prospectId && offerId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/offer/${offerId}/${offerRevision}`;
    } else if (prospectId && contractId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/case/${contractCaseId}/contract/${contractId}`;
    } else if (prospectId && leadId) {
      pathname = `/sales-management/customer/${prospectId}/contracts/lead/${leadId}`;
    } else if (prospectId) {
      pathname = `/sales-management/customer/${prospectId}`;
    } else if (contractId) {
      pathname = `/sales-management/case/${contractCaseId}/contract/${contractId}`;
    } else if (leadId) {
      pathname = `/sales-management/lead/${leadId}`;
    } else {
      pathname = `/sales-management/offer/${offerId}/${offerRevision}`;
    }
    history.push({
      pathname,
      state: { ...location.state },
    });
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (carData == null || isEmpty(carData)) {
    return (
      <EmptyState>
        <img src={Frame} alt="Test" />
        <h1>{t('Risk object not found')}</h1>
        <p>
          {`${t('We could not find the risk object with id')}: `}{' '}
          <strong>{riskObjectId}</strong>
        </p>
      </EmptyState>
    );
  }

  return (
    <MainContainer>
      <TopBar>
        <TopBarContainer>
          <TopBarLeft>
            <BackButton onClick={backButtonHandler} name={t('Previous page')} />
          </TopBarLeft>
          {!isCallantFeature && (
            <TopBarRight>
              <TopBarAction>
                <ActionButton
                  onClick={startNewTarrification}
                  disabled={!isProFlowEnabled}
                >
                  {t('New tarification')}
                </ActionButton>
              </TopBarAction>
            </TopBarRight>
          )}
        </TopBarContainer>
      </TopBar>

      <MainContent>
        <Sidebar>
          {sections.map(section => (
            <SubNavigation key={section.id}>
              <SubNavigation.Section>{section.label}</SubNavigation.Section>
              <SubNavigation.SectionContainer
                active={section.id === currentSection.section}
              >
                {section.subsections.map(subsection => (
                  <SubNavigation.SubSection
                    style={{ width: '20rem' }}
                    key={subsection.id}
                    onClick={() =>
                      handleSideBarClick(section.id, subsection.id)
                    }
                    active={
                      currentSection.section === section.id &&
                      currentSection.subsection === subsection.id
                    }
                  >
                    {subsection.label}
                  </SubNavigation.SubSection>
                ))}
              </SubNavigation.SectionContainer>
            </SubNavigation>
          ))}
        </Sidebar>

        <Content>
          {updateRequired && offerId && !contractId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object, would you like to save these changes and go back to the offer',
                )}
                <ActionButton onClick={() => handleSaveCarOfferChanges()}>
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          {updateRequired && contractId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object, would you like to save these changes and go back to the contract',
                )}
                <ActionButton onClick={() => handleSaveCarContractChanges()}>
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          {updateRequired &&
          (prospectId || leadId) &&
          !contractId &&
          !offerId ? (
            <StyledAlert icon type="normal">
              <StyledAlertContent>
                {t(
                  'You have made changes to the risk object, would you like to save these changes',
                )}
                <ActionButton onClick={() => handleSaveCarChanges()}>
                  {t(`Save`)}
                </ActionButton>
              </StyledAlertContent>
            </StyledAlert>
          ) : null}
          <section ref={refs[1][1]}>
            <CarInformation
              carData={updatedCarData}
              initEdit={editing ?? false}
              onSubmitFunction={carInformationValues =>
                carInformationSave(carInformationValues)
              }
              disableEditing={!canEditOffers}
              // disableEditing={
              //   (state != null && canEditOffers) ||
              //   (offerId != null && canEditOffers)
              //     ? false
              //     : true
              // }
            />
          </section>

          {/* <section ref={refs[1][2]}>
            <CarCategory
              isCabrio={carData?.is_cabrio}
              isJeep={carData?.is_jeep}
              isSportscar={carData?.is_sportscar}
            />
          </section> */}

          {/* <section ref={refs[1][3]}>
            <CarAssistance
              driveAssistanceSystem={carData?.drive_assistance_system}
            />
          </section> */}

          <section ref={refs[1][2]}>
            <DriversList
              onSubmitFunction={driversInformationValues =>
                carInformationSave(driversInformationValues)
              }
              disableEditing={!canEditOffers}
              mainDriver={
                updatedCarData?.drivers?.filter(({ primary }) => !!primary)[0]
              }
              secondaryDrivers={updatedCarData?.drivers?.filter(
                ({ primary }) => !primary,
              )}
            />
          </section>
        </Content>
      </MainContent>
    </MainContainer>
  );
};

const TopBarRight = styled.div`
  margin-left: 32px;
  flex: 1 0 auto;
  display: flex;
  align-items: center;
`;

const TopBarAction = styled.div`
  flex: 0 1 auto;
  padding-left: 25px;
  margin-left: auto;
  padding-top: 4px;
  padding-bottom: 4px;
  border-left: 2px solid #ccc;
`;

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  img {
    margin-left: 55px;
    margin-bottom: 50px;
  }

  h1 {
    font-family: ${({ theme }) => theme.font};
    font-weight: bold;
    font-size: 2.1rem;
    line-height: 135%;
    text-align: center;
    letter-spacing: -0.03em;
    color: black;
    margin-bottom: 10px;
  }

  p {
    font-family: ${({ theme }) => theme.font};
    font-size: 1.6rem;
    line-height: 135%;
    text-align: center;
    color: black;

    strong {
      font-weight: bold;
    }
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;

  > div {
    width: 100%;
  }
`;

const StyledAlertContent = styled.div`
  font-size: 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: auto;
`;

const LoadingContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  width: 100%;
  background-color: #f7f7f7;
  position: sticky;
  top: 8rem;
  z-index: 10;
  border-bottom: 1px solid #dbdbdb;

  @media screen and (max-width: 1240px) {
    top: 8rem;
  }
`;

const TopBarContainer = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  align-items: center;
  display: flex;
  padding: 12px 30px;
`;

const TopBarLeft = styled.div`
  flex: 0 0 220px;
`;

const MainContent = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  padding: 55px 30px;
  position: relative;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: sticky;
  top: 200px;
  margin-right: 32px;
  flex: 0 0 220px;

  & > button {
    margin-right: 0.78vw;
  }

  @media screen and (max-width: 1500px) {
    flex: 0 0 150px;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const Content = styled.div`
  flex: 0 1 auto;
  max-width: 100rem;
`;

export default CRMRiskObjectDetailCar;
